import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { styled } from "styled-components";
import { useDebouncedCallback } from "use-debounce";

import { knowledgeApiSlice } from "api/knowledgeApi.slice";
import { FilterButton, MasterDetailLayout } from "common/components";
import { UIInput } from "common/components/form/UIInput";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { activateModal } from "domains/modal/actions";
import { searchSlice } from "domains/search/search.slice";
import { VideoDetailSection } from "domains/videos/components/VideoDetailSection";
import { VideoList } from "domains/videos/components/VideoList";

const StyledInput = styled(UIInput)`
  margin-bottom: 1rem;
`;

const StyledSearchInputContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

export const VideoSearchPage = () => {
  const track = useTrack();
  const translate = useTranslate();
  const dispatch = useDispatch();
  const { videoId } = useParams();

  const isAuthenticated = useSelector(getIsAuthenticated);
  const currentSearchQuery = useSelector((state) => state.search.currentSearchQuery);
  const selectedSources = useSelector((state) => state.search.sources);

  const [searchInput, setSearchInput] = useState(currentSearchQuery);

  const { updateSearchQuery } = searchSlice.actions;

  const debouncedUpdateSearchQuery = useDebouncedCallback((searchQuery: string) => {
    dispatch(updateSearchQuery({ query: searchQuery }));
  }, 1000);

  useEffect(() => {
    debouncedUpdateSearchQuery(searchInput);
  }, [currentSearchQuery, debouncedUpdateSearchQuery, dispatch, searchInput, updateSearchQuery]);

  const {
    data: paginatedVideoSearchResults,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = knowledgeApiSlice.endpoints.searchVideos.useInfiniteQuery({
    searchQuery: currentSearchQuery,
    sourceNames: selectedSources.map((source) => source.name),
  });

  const videoSearchResults = paginatedVideoSearchResults?.pages.flat() ?? [];

  const video = videoId ? videoSearchResults.find((video) => video.id === videoId) : undefined;

  useEffect(() => {
    if (video)
      track({
        entityId: video.id,
        entityName: video.title,
        entityType: "Video",
        name: "Browse page",
        page: "Search",
        subpage: "Knowledge detail",
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, video?.id]);

  const loadNextPage = useCallback(async () => {
    if (hasNextPage && !isFetching) {
      await fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetching]);

  const getLink = (knowledgeId: string) => `/search/videos/${knowledgeId}`;

  const knowledgeList = (
    <VideoList
      videos={videoSearchResults.length === 0 && video ? [video] : videoSearchResults}
      selectedId={video?.id}
      trackPage="Discover"
      getToLink={getLink}
      showEmptyMessage={!!currentSearchQuery}
      emptyMessage={translate("app.search.results.no_results")}
      isPending={isFetching}
      hasNextPage={hasNextPage}
      loadNextPage={loadNextPage}
      lockTiles={!isAuthenticated}
    />
  );

  const shareLink = `${import.meta.env.VITE_WEBSITE_URL}${getLink(videoId ?? "")}`;

  const getFilterCount = () => {
    let count = 0;

    count += selectedSources.length;

    return count;
  };

  const openFilterModal = () => dispatch(activateModal({ id: "KnowledgeSearchFilter" }));

  return (
    <MasterDetailLayout
      headerElement={
        <StyledSearchInputContainer>
          <StyledInput
            clear={() => setSearchInput("")}
            name="videoSearch"
            onChange={setSearchInput}
            placeholder={translate("app.discover.topic.tabs.knowledge.filter.search_title")}
            value={searchInput}
          />
          <FilterButton count={getFilterCount()} onClick={openFilterModal} />
        </StyledSearchInputContainer>
      }
      listTitle={translate("app.discover.topic.tabs.videos.title")}
      listComponent={knowledgeList}
      listBackNavigation={{
        label: translate("app.search.title"),
        path: "/search",
      }}
      detailBackNavigation={{
        label: translate("app.discover.topic.tabs.videos.title"),
        path: "/search/videos",
      }}
      mainView={videoId ? "right" : "left"}
    >
      <VideoDetailSection
        id={video?.id}
        shareLink={shareLink}
        topicBack={{ term: "app.discover.topic.tabs.videos.title" }}
      />
    </MasterDetailLayout>
  );
};
